import {useInfiniteQuery} from 'react-query'
import {useRouter} from 'next/router'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useContext, useEffect, useMemo, useState} from 'react'
import Link from 'next/link'
import {Pagination} from '@/products/flexible-web-toolkit/components/pagination'
import {Button} from '@/products/flexible-web-toolkit/components/button/button'
import {SkeletonLoader} from '@/products/flexible-web-toolkit/components/skeleton-loader'
import {pushToDataLayer} from '@/utilities/analytics'
import type {
	Tersed,
	Block,
	TaxonomySpeciesContentItem,
	ArticleConfigContentItem,
} from '@/services/kontent-ai/types'
import {fetchListerItems} from '@/services/lister-items/client'
import type {ListerItemsQueryKey} from '@/products/flexible-web-toolkit/blocks/module-coupon-lister/types'
import {useAuth} from '@/products/auth/auth-provider'
import {GlobalContext} from '@/services/global-client-state'
import type {GlobalState} from '@/models/global-state.ts'
import {textChecker} from '@/utilities/rich-text'
import {Card} from '@/products/flexible-web-toolkit/components/card'
import {CardImage} from '@/products/flexible-web-toolkit/components/card/card-image'
import {ListerSorting} from '../lister-sorting'
import {dropDownItems, sortItems} from '../lister-helper'
import type {ListerSortingContentItem, SortingCriterion} from '../lister-model'

export type ModuleArticleListerContentItem = IContentItem<{
	articleListerTitle: Elements.TextElement
	initialArticlesNumber: Elements.NumberElement
	subsequentArticlesNumber: Elements.NumberElement
	buttonText: Elements.TextElement
	nextButtonText: Elements.TextElement
	prevButtonText: Elements.TextElement
	usePagination: Elements.MultipleChoiceElement
	itemsOrderingOptions: Elements.MultipleChoiceElement
	moduleArticleListerSorting: Elements.LinkedItemsElement<ListerSortingContentItem>
}>

function buildArticleFilters(
	speciesCodename: string | undefined,
	articleFilterOptions: GlobalState['articlesFilterOptions']
): {taxonomy: string; terms: string[]}[] {
	const speciesFilter = [
		{
			taxonomy: 'species_taxonomy',
			terms: [speciesCodename],
		},
	]
		.map((item) => ({
			...item,
			terms: item.terms.filter(Boolean),
		}))
		.filter((item) => item.terms.length > 0)

	const updatedFilters = Object.entries(articleFilterOptions).map(
		([taxonomy, options]) => ({
			taxonomy: taxonomy === 'species' ? 'species_taxonomy' : taxonomy,
			terms: (() => {
				if ('checkboxOptions' in options) {
					return options.checkboxOptions
						.filter((opt) => opt.checked)
						.map((opt) => opt.codename)
				}

				return [options.value]
			})(),
		})
	)

	return [...speciesFilter, ...updatedFilters]
}

export const ModuleArticleListerBlock: Block<
	ModuleArticleListerContentItem
> = ({block, page, globalConfig}) => {
	const serverRenderedBlock = block as typeof block & {
		initialItems?: Tersed<ArticleConfigContentItem>[]
		initialTotalCount?: number
	}
	const router = useRouter()
	const gs = useContext(GlobalContext)
	const authState = useAuth()
	const {page: currentPage} = router.query
	const serverRenderedInitialItems = serverRenderedBlock.initialItems

	const [sortingCriterion, setSortingCriterion] = useState<
		SortingCriterion | undefined
	>()
	const speciesCodename =
		'filterByTaxonomy' in page.elements
			? (
					page.elements
						.filterByTaxonomy as Tersed<TaxonomySpeciesContentItem>[]
				)[0]?.elements.species[0]?.codename
			: undefined
	const [filters, setFilters] = useState<
		{
			taxonomy: string
			terms: string[]
		}[]
	>(
		buildArticleFilters(
			speciesCodename,
			gs?.[0]?.articlesFilterOptions ?? {}
		)
	)

	const {buttonText} = block.elements
	const nextButtonText = block.elements.nextButtonText || 'Next'
	const prevButtonText = block.elements.prevButtonText || 'Prev'
	const subsequentArticlesNumber =
		block.elements.subsequentArticlesNumber ?? 6
	const initialItemsNumber = block.elements.initialArticlesNumber ?? 6

	const [limitNumberWithSubSeq, setLimitNumberWithSubSeq] =
		useState(initialItemsNumber)

	useEffect(() => {
		setFilters(
			buildArticleFilters(
				speciesCodename,
				gs?.[0]?.articlesFilterOptions ?? {}
			)
		)
		setLimitNumberWithSubSeq(initialItemsNumber)
	}, [gs, initialItemsNumber, speciesCodename])

	const usePagination = block.elements.usePagination[0]?.codename === 'yes'
	const userRoles = authState.userDetails.userRoles ?? []
	const {isLoading, isFetching, fetchNextPage, data} = useInfiniteQuery({
		queryKey: [
			'moduleArticleLister',
			{
				codename: 'config_article',
				locale: router.locale ?? '',
				filters: JSON.stringify(filters),
				orderBy:
					block.elements.itemsOrderingOptions[0]?.codename ??
					undefined,
				hideElementCodename: 'hide_from_lister',
				skip:
					(Number(currentPage?.toString() ?? 1) - 1) *
					initialItemsNumber,
				limit: limitNumberWithSubSeq,
				roles: userRoles.join(','),
			},
		] as ListerItemsQueryKey,
		queryFn: (opts) =>
			fetchListerItems<Tersed<ArticleConfigContentItem>>(opts),
		staleTime: 10 * (60 * 1000),
		cacheTime: 15 * (60 * 1000),
		enabled: globalConfig.elements.cdcKey
			? (authState.ready && !authState.authenticated) ||
				(authState.ready &&
					authState.authenticated &&
					Boolean(authState.userDetails.id))
			: true,
	})
	const fetchedArticleCards = useMemo(
		() => data?.pages.flatMap((item) => item.items),
		[data]
	)
	const displayedArticleCards = useMemo(
		() =>
			sortItems<Tersed<ArticleConfigContentItem>>(
				[...(fetchedArticleCards ?? serverRenderedInitialItems ?? [])],
				sortingCriterion
			),
		[fetchedArticleCards, serverRenderedInitialItems, sortingCriterion]
	)

	const totalCount =
		data?.pages[0]?.pagination.totalCount ??
		serverRenderedBlock.initialTotalCount ??
		0
	const shouldShowInitialSkeleton =
		!data && displayedArticleCards.length === 0
	const isAwaitingAuthenticatedHydration =
		authState.authenticated &&
		(!authState.ready || !authState.userDetails.id || isFetching)
	const shouldShowAuthenticatedRefreshSkeleton =
		isAwaitingAuthenticatedHydration &&
		!data &&
		(serverRenderedInitialItems?.length ?? 0) > 0
	const shouldShowFetchingSkeleton =
		isFetching && Boolean(data) && displayedArticleCards.length > 0
	const shouldShowArticleCards =
		((!isLoading && data) || displayedArticleCards.length > 0) &&
		!shouldShowAuthenticatedRefreshSkeleton
	const onPageChange = (nextPage: number): void => {
		const currentQuery = new URLSearchParams(
			router.asPath.split('?')[1] || ''
		)
		currentQuery.set('page', nextPage.toString())

		void router.push({
			pathname: `${router.locale ?? ''}${router.asPath.split('?')[0]}`,
			query: currentQuery.toString(),
		})
	}

	function getDisplayedItemCount(): number {
		return displayedArticleCards.length
	}

	function getHasNextPage(): boolean {
		const displayedItems = getDisplayedItemCount()

		return displayedItems < totalCount
	}

	const handleSortingLister = (
		e: React.ChangeEvent<HTMLSelectElement>
	): void => {
		setSortingCriterion(e.target.value as SortingCriterion)
	}

	return (
		<div>
			{shouldShowInitialSkeleton ||
			shouldShowAuthenticatedRefreshSkeleton ? (
				<div className="mx-auto my-2 grid max-w-screen-xl grid-cols-1 gap-10 px-3 sm:grid-cols-2 sm:px-0 md:grid-cols-3">
					<SkeletonLoader amount={initialItemsNumber} />
				</div>
			) : null}
			{shouldShowArticleCards ? (
				<>
					{block.elements.moduleArticleListerSorting[0] ? (
						<div className="mx-auto flex max-w-screen-xl flex-wrap justify-end">
							<ListerSorting
								className="w-full px-4 sm:w-auto"
								codename={
									block.elements.moduleArticleListerSorting[0]
										?.system.codename
								}
								dropDownData={dropDownItems(
									block.elements.moduleArticleListerSorting[0]
								)}
								id={
									block.elements.moduleArticleListerSorting[0]
										?.system.id || 'sorting_article_listers'
								}
								label={textChecker(
									block.elements.moduleArticleListerSorting[0]
										?.elements.defaultSortingText.value ||
										'',
									'Sort By'
								)}
								onChange={handleSortingLister}
							/>
						</div>
					) : null}

					<div className="mx-auto flex max-w-screen-xl flex-wrap justify-center">
						{displayedArticleCards
							.filter(authState.checkUserRoleForBlock)
							.map((item) => (
								<div
									className="my-2 flex w-full flex-wrap justify-center sm:w-1/2 md:w-1/3"
									key={item.system.id}
								>
									<Card
										href={item.elements.articleSlug}
										title={
											item.elements.articleSlug ? (
												<Link
													href={
														item.elements
															.articleSlug
													}
													onClick={() => {
														pushToDataLayer({
															event: 'card_click',
															element_text:
																item.elements
																	.title,
															link_url:
																item.elements
																	.articleSlug,
														})
													}}
													prefetch={false}
													rel="noreferrer"
												>
													{}

													{item.elements.title}
												</Link>
											) : null
										}
									>
										{item.elements.thumbnailImage[0] ? (
											<CardImage
												alt={
													item.elements
														.thumbnailImage[0]
														.description
												}
												className="h-44 object-cover"
												src={
													item.elements
														.thumbnailImage[0].url
												}
											/>
										) : null}
									</Card>
								</div>
							))}
					</div>
				</>
			) : null}

			{shouldShowFetchingSkeleton ? (
				<div className="mx-auto my-2 grid max-w-screen-xl grid-cols-1 gap-10 px-3 sm:grid-cols-2 sm:px-0 md:grid-cols-3">
					<SkeletonLoader amount={subsequentArticlesNumber} />
				</div>
			) : null}
			{usePagination && totalCount > 0 ? (
				<Pagination
					activeIndex={Number(currentPage) || 1}
					amountOfPages={Math.ceil(totalCount / initialItemsNumber)}
					nextButtonText={<span>{nextButtonText}</span>}
					onPageChange={onPageChange}
					pagesToShow={3}
					prevButtonText={<span>{prevButtonText}</span>}
					showLastPage
				/>
			) : (
				buttonText &&
				getHasNextPage() && (
					<div
						className="my-10 text-center"
						data-kontent-element-codename="button_text"
					>
						<Button
							onClick={async () => {
								setLimitNumberWithSubSeq(
									limitNumberWithSubSeq +
										subsequentArticlesNumber
								)
								await fetchNextPage()
								pushToDataLayer({
									event: 'cta_click',
									cta_name: buttonText,
									cta_category: 'button',
								})
							}}
							type="button"
						>
							{buttonText}
						</Button>
					</div>
				)
			)}
		</div>
	)
}
